import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchInvitations,
  inviteMember,
  cancelInvitation,
} from '@/lib/api/team-members'
import type { OrgRole } from '@/types/team-members'
import { toast } from 'sonner'

/**
 * Query key factory for invitations
 */
export const invitationKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationKeys.all, 'list'] as const,
  list: (organizationId: string) =>
    [...invitationKeys.lists(), organizationId] as const,
}

/**
 * Fetch all pending invitations for current organization
 */
export function useInvitations() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: invitationKeys.list(organizationId || ''),
    queryFn: () => fetchInvitations(organizationId!),
    enabled: !!organizationId,
  })
}

/**
 * Invite a new member to the organization
 */
export function useInviteMember() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: { email: string; role: OrgRole }) =>
      inviteMember(currentOrganization!.id, data.email, data.role),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.list(currentOrganization!.id),
      })
      toast.success('Invitation sent successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to send invitation', {
        description: error.message,
      })
    },
  })
}

/**
 * Cancel a pending invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invitationKeys.list(currentOrganization!.id),
      })
      toast.success('Invitation cancelled successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to cancel invitation', {
        description: error.message,
      })
    },
  })
}
